import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, CheckIcon } from '@heroicons/react/24/outline';
import { fetchPatientCard } from '../../../../services/patientCard';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { copyToClipboard } from '../../../../utils/clipboard';
import {
    createSparkRefinement,
    getUnsubmittedSparkRefinement
} from '../../../../services/sparkSubmission';
import { type RegisterFooter } from './spark.shared';

//  Step 1 — create a refinement case in Spark off the patient's current
//  (parent) case. Unlike the primary "Create case" step there is no search /
//  link / unlink: the parent is the stored manufacturer case, and the new
//  refinement case id is held only in wizard state (never persisted).

export function CreateRefinementStep({
    patientId,
    card,
    caseId,
    link,
    onReady,
    registerFooter
}: {
    patientId: string;
    card: Awaited<ReturnType<typeof fetchPatientCard>> | undefined;
    caseId: string | null;
    link: string | null;
    onReady: (caseId: string, link: string) => void;
    registerFooter: RegisterFooter;
}) {
    const { showToast } = useToast();
    const alreadyCreated = Boolean(caseId);
    const [busy, setBusy] = useState(false);
    // Detect (once) whether an unsubmitted refinement already exists, so we resume
    // it instead of creating a duplicate. Skip when a case is already in wizard
    // state (fresh create earlier this session, or the user navigated back here).
    const [checking, setChecking] = useState(!alreadyCreated);
    const checkStartedRef = useRef(false);

    useEffect(() => {
        if (alreadyCreated) {
            setChecking(false);
            return;
        }
        if (checkStartedRef.current) return;
        checkStartedRef.current = true;

        let cancelled = false;
        void (async () => {
            try {
                const existing = await getUnsubmittedSparkRefinement(patientId);
                if (cancelled) return;
                if (existing) {
                    showToast('Resumed an existing unsubmitted refinement.', 'success');
                    // Sets the caseId in the wizard and advances to Photos; this step
                    // then unmounts, so leave `checking` true to keep the create UI hidden.
                    onReady(existing.caseId, existing.link);
                    return;
                }
            } catch (err) {
                // Fail open — never block the user from creating a refinement.
                if (!cancelled) {
                    showToast(
                        apiErrorMessage(
                            err,
                            'Could not check for an existing refinement; you can create a new one.'
                        ),
                        'error'
                    );
                }
            }
            if (!cancelled) setChecking(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [alreadyCreated, patientId, onReady, showToast]);

    const handleCreate = useCallback(async () => {
        setBusy(true);
        try {
            const res = await createSparkRefinement(patientId);
            showToast('Spark refinement created.', 'success');
            onReady(res.caseId, res.link);
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to create the Spark refinement.'), 'error');
            setBusy(false);
        }
    }, [patientId, onReady, showToast]);

    const onPrimary = useCallback(() => {
        if (alreadyCreated) {
            onReady(caseId!, link ?? '');
            return;
        }
        void handleCreate();
    }, [alreadyCreated, caseId, link, onReady, handleCreate]);

    const primaryLabel = checking
        ? 'Checking…'
        : alreadyCreated
          ? 'Continue'
          : busy
            ? 'Creating…'
            : 'Create refinement & continue';

    useEffect(() => {
        registerFooter({ label: primaryLabel, onClick: onPrimary, disabled: busy || checking });
        return () => registerFooter(null);
    }, [registerFooter, primaryLabel, onPrimary, busy, checking]);

    const fullName = card
        ? `${card.firstName ?? ''} ${card.lastName ?? ''}`.trim() || card.name
        : '';
    const dob = card?.dateOfBirth ? card.dateOfBirth.slice(0, 10) : '—';
    const gender = card?.gender ?? '—';
    const sparkAcct = card?.practice?.sparkAcct ?? null;
    const parentCase = card?.treatment?.manufacturerCaseId ?? null;
    const parentLink = card?.treatment?.manufacturerLink ?? null;

    if (checking) {
        return (
            <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center gap-3 py-20 text-gray-500">
                <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                <p className="text-sm">Checking for an existing refinement…</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            {/* Patient identity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Patient identity
                </p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Field label="Name" value={fullName} />
                    <Field label="Date of birth" value={dob} />
                    <Field label="Gender" value={gender} />
                    <Field label="Spark account" value={sparkAcct ?? '—'} />
                </dl>
            </div>

            {/* Parent case the refinement is created against */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Parent case (the refinement is created off this case)
                </p>
                <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-600">
                        Case ID:{' '}
                        <span className="font-mono text-gray-900">{parentCase ?? '—'}</span>
                    </span>
                    {parentCase && <CopyButton value={parentCase} label="Copy ID" />}
                </div>
                {parentLink && (
                    <div className="flex items-center justify-between gap-3 min-w-0 mt-2">
                        <a
                            href={parentLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 min-w-0 text-xs text-[#3773A8] font-medium hover:underline"
                        >
                            <span className="truncate">{parentLink}</span>
                            <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0" />
                        </a>
                        <CopyButton value={parentLink} label="Copy link" />
                    </div>
                )}
                <p className="text-[11px] text-gray-400 mt-3">
                    The parent case must be Shipped (or partially shipped) for Spark to accept a
                    refinement.
                </p>
            </div>

            {alreadyCreated && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                        <CheckIcon className="w-4 h-4" /> Refinement created
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-600">
                            Refinement case ID:{' '}
                            <span className="font-mono text-gray-900">{caseId}</span>
                        </span>
                        <CopyButton value={caseId!} label="Copy ID" />
                    </div>
                    {link && (
                        <div className="flex items-center justify-between gap-3 min-w-0">
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 min-w-0 text-xs text-[#3773A8] font-medium hover:underline"
                            >
                                <span className="truncate">{link}</span>
                                <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0" />
                            </a>
                            <CopyButton value={link} label="Copy link" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-col">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {label}
            </dt>
            <dd className="text-sm text-gray-800">{value || '—'}</dd>
        </div>
    );
}

//  Copy-to-clipboard pill (mirrors CreateCaseStep's CopyButton)

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
    const [copied, setCopied] = useState(false);
    const onClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!(await copyToClipboard(value))) return;
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };
    const base =
        'inline-flex items-center gap-1 px-2 py-[3px] rounded-md border text-[11px] font-medium transition-colors whitespace-nowrap flex-shrink-0';
    const state = copied
        ? 'border-green-500 text-green-700 bg-green-50'
        : 'border-gray-300 text-gray-500 bg-white hover:border-blue-500 hover:text-blue-600';
    return (
        <button type="button" onClick={onClick} className={`${base} ${state}`}>
            {copied ? (
                <>
                    <CheckIcon className="w-3 h-3" /> Copied
                </>
            ) : (
                label
            )}
        </button>
    );
}
